import Sidebar from '../components/SideBar';
import '../styles/configuracoesStyle/Configuracoes.css';
import { User } from 'lucide-react';
import React, { useState, useEffect, FormEvent } from 'react';
import { employeeService } from '../API/employeeService';
import { authService } from '../API/auth';
import UserForm from '../components/configuracoes/UserForm';
import EmployeeList from '../components/configuracoes/EmployeeList';
import AuditInfo from '../components/configuracoes/AuditInfo';
import LogoutButton from '../components/configuracoes/LogoutButton';
import UserStaticInfo from '../components/configuracoes/UserStaticInfo';
import PasswordModal from '../components/configuracoes/PasswordModal';

interface Employee {
  id: number;
  nome: string;
  email: string;
  empresa: string;
  cargo: string;
}

export default function Configuracoes() {
  // ROLE agora baseado no cargo do membership
  const [role, setRole] = useState<'funcionario' | 'admin' | null>(null);

  // ID do assinante → necessário para adicionar funcionários
  const [assinanteId, setAssinanteId] = useState<number | null>(null);

  // Dados pessoais
  const [userNome, setUserNome] = useState('');
  const [userSenha, setUserSenha] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userEmpresa, setUserEmpresa] = useState('');
  const [userCargo, setUserCargo] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [editMode, setEditMode] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordToConfirmDelete, setPasswordToConfirmDelete] = useState("");
  const [employeeToDelete, setEmployeeToDelete] = useState<number | null>(null);
  const [maxMembros, setMaxMembros] = useState<number | null>(null);



  // Auditoria
  const [auditData, setAuditData] = useState({
    criadoEm: "",
    ultimaModificacao: "",
    modificadoPor: "",
    status: ""
  });

  // Funcionários renderizados no front-end
  const [funcionarios, setFuncionarios] = useState<Employee[]>([]);

  // Formulário de membros
  const [funcNome, setFuncNome] = useState('');
  const [funcSenha, setFuncSenha] = useState('');
  const [funcEmail, setFuncEmail] = useState('');
  const [funcEmpresa, setFuncEmpresa] = useState('');
  const [funcCargo, setFuncCargo] = useState('');

  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [adminPasswordForEmployeeActions, setAdminPasswordForEmployeeActions] = useState('');


  // ======================================================
  // 🔹 CARREGAR PERFIL REAL
  // ======================================================
  useEffect(() => {
    async function loadProfile() {
      try {
        const user = await authService.getProfile();

        setUserNome(user.nome_completo);
        setUserEmail(user.email);

        // Empresa → sempre vem de empresa_membership.empresa_nome
        setUserEmpresa(user.empresa_membership?.empresa_nome || "");

        // Cargo → vem do membership
        setUserCargo(user.assinante_membership?.cargo || "");

        // ROLE CORRETA:
        setRole(user.assinante_membership?.cargo === "ADMIN" ? "admin" : "funcionario");

        // Guardar assinante_id (necessário para cadastrar funcionários)
        setAssinanteId(user.assinante_membership?.assinante_id || null);

        setMaxMembros(user.empresa_membership?.plano?.max_membros || null);

        // Auditoria
        setAuditData({
          criadoEm: new Date(user.created).toLocaleString("pt-BR"),
          ultimaModificacao: new Date(user.modified).toLocaleString("pt-BR"),
          modificadoPor: user.nome_completo,
          status: user.status_vinculo || "ATIVO",
        });

      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
        alert("Erro ao carregar informações do usuário.");
        setRole("funcionario");
      }
    }
    loadProfile();
  }, []);

  useEffect(() => {
    async function loadProfileAndEmployees() {
      try {
        const user = await authService.getProfile();

        setAssinanteId(user.assinante_membership?.assinante_id || null);
          const empresaId = user.empresa_membership?.empresa_id;
        if (empresaId) {
          const list = await employeeService.listEmployees(empresaId);
          setFuncionarios(list);
        }
      } catch (e) {
        console.error(e);
      }
    }

    loadProfileAndEmployees();
  }, []);


  // ======================================================
  // 🔹 SALVAR DADOS PESSOAIS
  // ======================================================
  const handleSaveUser = async (e: FormEvent) => {
    e.preventDefault();

    // 🚨 OBRIGA a senha atual para qualquer atualização
    if (!currentPassword || currentPassword.trim() === "") {
      alert("Você precisa informar sua senha atual para atualizar qualquer informação.");
      return;
    }

    try {
      await authService.updateProfile({
        nome_completo: userNome,
        email: userEmail,
        new_password: userSenha || undefined,
        current_password: currentPassword,
      });

      alert("Dados atualizados com sucesso!");

    } catch (error: any) {
      console.error(error);
      alert("Erro ao atualizar perfil.");
    }
  };


  // ======================================================
  // 🔹 ADICIONAR FUNCIONÁRIO
  // ======================================================
  const handleSaveEmployee = async (e: FormEvent) => {
    e.preventDefault();

    if (!assinanteId) {
      alert("Erro: assinante_id não encontrado no profile!");
      return;
    }

    if (!currentPassword) {
      alert("Digite sua senha atual para realizar esta ação.");
      return;
    }
    

    try {
      const payload = {
        email: funcEmail,
        nome_completo: funcNome,
        password: funcSenha,      // senha nova do funcionário
        cargo: funcCargo,
        current_password: currentPassword // SENHA DO ADMIN
      };

      if (editingEmployee) {
        await employeeService.updateEmployee(assinanteId, editingEmployee.id, payload);
        alert("Funcionário atualizado com sucesso!");
      } else {
        await employeeService.addEmployee(assinanteId, payload);
        alert("Funcionário criado com sucesso!");
      }
      await refreshEmployees(); // 👈 atualiza automaticamente

      // limpar
      setShowEmployeeForm(false);
      setEditingEmployee(null);
      setCurrentPassword('');

    } catch (error) {
      alert("Erro ao salvar funcionário.");
      console.error(error);
    }
  };


  // ======================================================
  // 🔹 EDITAR
  // ======================================================
  const handleEditEmployee = (id: number) => {
    const emp = funcionarios.find(f => f.id === id);
    if (emp) {
      setEditingEmployee(emp);
      setFuncNome(emp.nome);
      setFuncEmail(emp.email);
      setFuncEmpresa(emp.empresa);
      setFuncCargo(emp.cargo);
      setFuncSenha('');
      setShowEmployeeForm(true);
    }
  };

  // ======================================================
  // 🔹 ADICIONAR NOVO
  // ======================================================
  const handleAddEmployee = () => {
    if (maxMembros !== null && funcionarios.length >= maxMembros) {
      alert(`Você atingiu o limite máximo de ${maxMembros} membros do seu plano.`);
      return;
    }

    setEditingEmployee(null);
    setFuncNome('');
    setFuncEmail('');
    setFuncEmpresa(userEmpresa);
    setFuncCargo('');
    setFuncSenha('');
    setShowEmployeeForm(true);
  };

    const handleDeleteEmployee = (id: number) => {
      const emp = funcionarios.find(f => f.id === id);

      if (emp?.cargo === "ADMIN") {
        alert("Você não pode deletar outro administrador.");
        return;
      }

      setEmployeeToDelete(id);
      setPasswordToConfirmDelete("");
      setShowPasswordModal(true);
    };

  const confirmDeleteEmployee = async () => {
    if (!assinanteId || !employeeToDelete) return;

    try {
      await employeeService.deleteEmployee(
        assinanteId,
        employeeToDelete,
        passwordToConfirmDelete
      );

      setFuncionarios(prev => prev.filter(f => f.id !== employeeToDelete));

      alert("Funcionário removido com sucesso!");

    } catch (error) {
      console.error(error);
      alert("Erro ao excluir funcionário. Verifique sua senha.");
    }

    setShowPasswordModal(false);
  };

  const refreshEmployees = async () => {
    if (!assinanteId) return;
    const list = await employeeService.listEmployees(assinanteId);
    setFuncionarios(list);
  };

  if (!role) return <div>Carregando...</div>;

  return (
    <div style={{ display: 'flex', background: '#f5f5f5' }}>

        {showPasswordModal && (
          <PasswordModal
            password={passwordToConfirmDelete}
            setPassword={setPasswordToConfirmDelete}
            onConfirm={confirmDeleteEmployee}
            onCancel={() => setShowPasswordModal(false)}
          />
        )}

      <Sidebar />
      <div className="config-container">
        <div className="config-row">
          <h2>Configurações</h2>
          <LogoutButton />
        </div>

        <p>Gerencie as suas configurações internas.</p>

        <div className="config-card">
          <div className="card-header-config">
            <User className="card-icon" />
            <h3 className="card-title-config">Informações pessoais</h3>
          </div>

          {!editMode ? (
            <>
              <UserStaticInfo
                nome={userNome}
                email={userEmail}
                empresa={userEmpresa}
                cargo={userCargo}
              />

              <div style={{ marginTop: "20px" }}>
                <button className="btn-submit" onClick={() => setEditMode(true)}>
                  Editar informações
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleSaveUser}>
              <UserForm
                nome={userNome}
                setNome={setUserNome}
                senha={userSenha}
                setSenha={setUserSenha}
                currentPassword={currentPassword}
                setCurrentPassword={setCurrentPassword}
                email={userEmail}
                setEmail={setUserEmail}
                empresa={userEmpresa}
                setEmpresa={setUserEmpresa}
                cargo={userCargo}
                setCargo={setUserCargo}
                role={role}
              />

              <div className="form-buttons">
                <button type="submit" className="btn-submit">Salvar</button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setEditMode(false);
                    setUserSenha("");
                    setCurrentPassword("");
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <AuditInfo data={auditData} />

          {role === 'admin' && (
            <>
              {!showEmployeeForm && (
                <EmployeeList
                  funcionarios={funcionarios}
                  onEdit={handleEditEmployee}
                  onDelete={handleDeleteEmployee}
                  onAdd={handleAddEmployee}
                />
              )}

              {showEmployeeForm && (
                <form onSubmit={handleSaveEmployee}>
                  <UserForm
                    nome={funcNome}
                    setNome={setFuncNome}

                    senha={funcSenha}
                    setSenha={setFuncSenha}

                    currentPassword={currentPassword}     // senha atual DO ADMIN
                    setCurrentPassword={setCurrentPassword}

                    email={funcEmail}
                    setEmail={setFuncEmail}

                    empresa={funcEmpresa}
                    setEmpresa={setFuncEmpresa}

                    cargo={funcCargo}
                    setCargo={setFuncCargo}

                    role={role}
                  />

                  <div className="form-buttons">
                    <button type="submit" className="btn-submit">Salvar</button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setShowEmployeeForm(false);
                        setEditingEmployee(null);
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
